import React, { useEffect, useMemo, useState } from "react";
import { Box, CssBaseline, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DetailsPageHeader from "../../../../../common/DetailsPageHeader";
import GeneralAdminSidebar from "../../../../../common/GeneralAdminSidebar";
import { GeneralAdminRoutes, mainLayoutStyles, showNotification } from "../../../../../constants";
import { useAuth } from "../../../../../hooks";
import { addGroup } from "../../hooks/GAGroupsHooks";
import { Toast } from "../../../../../common/alert";
import QTDetailsImageWithLabelSection from "../../../../../common/QTDetailsImageWithLabelSection";
import QTDetailsAutoCompleteWithLabelSection from "../../../../../common/QTDetailsAutoCompleteWithLabelSection";
import QTDetailsTextFieldWithLabelSection from "../../../../../common/QTDetailsTextFieldWithLabelSection";
import { useContributorsContext } from "../../../../../providers/OrganizationAdmin/ContributorsProvider";
import { images } from "../../../../../constants";
import { uploadGroupImageToStorage } from "../../../../../modules/OrganizationAlbums/hooks/AlbumsHooks";

const GAAddGroupContainer = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { GAUsers, handleGetAllGAUsers } = useContributorsContext();

  const [loading, setLoading] = useState(false);
  const [editEnabled, setEditEnabled] = useState(true);
  const [createdByError, setCreatedByError] = useState(false);
  const [uploadImage, setUploadImage] = useState(null);
  const [group, setGroup] = useState({
    name: "",
    description: "",
    status: "active", // stored lowercase
    createdBy: "", // required - user id
    createdByData: null,
    imageUrls: [],
    originalPhotoName: "",
  });

  useEffect(() => {
    if (GAUsers && GAUsers?.length > 0) return;
    handleGetAllGAUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createdByOptions = useMemo(() => {
    const users = Array.isArray(GAUsers) ? GAUsers : [];

    // Keep only active users (users.isActive !== false) and filter out any Guest users
    return users
      .filter((u) => u)
      .filter((u) => {
        // canonical isActive flag (inactive users should not appear)
        return u?.isActive !== false;
      })
      .filter((u) => {
        const first = (u?.firstName || "").trim().toLowerCase();
        const last = (u?.lastName || "").trim().toLowerCase();
        return !(first === "guest" && last === "user");
      });
  }, [GAUsers]);

  // Default Created By to the currently logged-in admin's userId (not admins.id)
  useEffect(() => {
    const currentUserId = currentUser?.userId;
    if (!currentUserId) return;
    if (group.createdBy) return;

    const match = createdByOptions.find((u) => u?.id === currentUserId);
    if (!match) return;

    setGroup((p) => ({
      ...p,
      createdBy: match.id,
      createdByData: match,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.userId, createdByOptions]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (!group.name?.trim()) {
        Toast("error", "Group name is required.");
        return;
      }

      const status = (group.status || "").toLowerCase();
      if (status !== "active" && status !== "inactive" && status !== "draft") {
        Toast("error", "Status must be 'active', 'draft', or 'inactive'.");
        return;
      }

      if (!group.createdBy) {
        setCreatedByError(true);
        Toast("error", "Created By is required.");
        return;
      }

      setCreatedByError(false);

      const payload = {
        name: group.name.trim(),
        description: group.description || "",
        status,
        createdAt: new Date(),
        createdBy: group.createdBy,
        members: [group.createdBy].filter(Boolean),
        quicktales: [],
        isCreatedBy: "admin",
      };

      // Upload group image (if provided) and store on group.imageUrls[0]
      if (uploadImage != null) {
        const photoUrl = await uploadGroupImageToStorage(uploadImage?.raw);
        payload.imageUrls = [photoUrl];
        payload.originalPhotoName = uploadImage?.raw?.name;
      }

      const res = await addGroup(payload);
      showNotification("Group added successfully.");
      navigate(GeneralAdminRoutes.groupDetails, {
        state: { groupDetails: { id: res.id, ...payload } },
      });
    } catch (error) {
      Toast("error", error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={`Add Group`}
        bgColor="#fff"
        buttonText={"Continue Editing"}
        editEnabled={editEnabled}
        setEditEnabled={setEditEnabled}
        cancelButton={true}
        onCancelClick={() => navigate(GeneralAdminRoutes.groups)}
        submitButtonText={"Add Group"}
        handleSave={handleSubmit}
        loading={loading}
      />

      <GeneralAdminSidebar />

      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"} style={subContainerStyles}>
          <QTDetailsImageWithLabelSection
            label={"Group Photo"}
            image={
              group?.imageUrls?.[0]
                ? group.imageUrls[0]
                : images.QuicktaleLogo
            }
            fileName={group?.originalPhotoName}
            handleImagePreviewModalOpen={() => {}}
            editEnabled={true}
            details={group}
            setDetails={setGroup}
            uploadImage={uploadImage}
            setUploadImage={setUploadImage}
            isCreateMode={true}
            autoOpenCropOnUpload={true}
          />

          <QTDetailsTextFieldWithLabelSection
            label={"Group Name"}
            value={group.name}
            disabled={true}
            editEnabled={true}
            onChange={(e) => setGroup((p) => ({ ...p, name: e.target.value }))}
          />

          <QTDetailsAutoCompleteWithLabelSection
            label={"Created By"}
            value={
              group?.createdByData != null
                ? (() => {
                    const first = group?.createdByData?.firstName || "";
                    const last = group?.createdByData?.lastName || "";
                    const userName = group?.createdByData?.userName || "";
                    const displayName = `${first} ${last}`.trim();
                    return userName
                      ? `@${userName}/${displayName}`
                      : displayName;
                  })()
                : group?.createdBy || ""
            }
            onChange={(docData) => {
              if (docData?.id) {
                setCreatedByError(false);
                setGroup((p) => ({
                  ...p,
                  createdByData: docData,
                  createdBy: docData.id,
                }));
              } else {
                setGroup((p) => ({
                  ...p,
                  createdByData: null,
                  createdBy: "",
                }));
              }
            }}
            disabled={true}
            optionsData={createdByOptions}
            editEnabled={true}
            error={createdByError}
          />

          <QTDetailsTextFieldWithLabelSection
            label={"Status"}
            value={
              editEnabled
                ? group?.status
                  ? group.status.charAt(0).toUpperCase() + group.status.slice(1).toLowerCase()
                  : ""
                : group?.status
            }
            disabled={true}
            editEnabled={true}
            select={true}
            dropdownValues={["Active", "Draft", "Inactive"]}
            onChange={(e) => setGroup((p) => ({ ...p, status: (e.target.value || "").toLowerCase() }))}
          />

          <QTDetailsTextFieldWithLabelSection
            label={"Description"}
            value={group.description}
            disabled={true}
            multiline={true}
            rows={5}
            editEnabled={true}
            onChange={(e) => setGroup((p) => ({ ...p, description: e.target.value }))}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default GAAddGroupContainer;

const subContainerStyles = {
  paddingLeft: 30,
  paddingRight: 30,
  marginTop: 50,
  width: "100%",
};

const labelStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 600,
  marginBottom: 6,
};

const inputStyles = {
  width: 520,
  maxWidth: "100%",
  height: 36,
  borderRadius: 6,
  border: "1px solid #D0D0D0",
  paddingLeft: 10,
  fontFamily: "Avenir Book, sans-serif",
};
